import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/api';
import { Icons } from '../components/shared';
import MessageContent from '../components/shared/MessageContent';

// Import Book Advertisement
import { SmallBookAd, MicroBookAd } from '../components/Ads/BookAdvertisements';

// Import Search Controls for chat rooms
import { 
  DatabaseToggle, 
  InteractiveMapPanel, 
  CategoryCheckboxes,
  URLPreferenceFilters 
} from '../components/shared/SearchControlsPanel';

// Import Cockpit Dashboard — embeds search, map, stats, analytics, duplicates below chat
import CockpitDashboard from '../components/CockpitDashboard';
import InstallAppButton from '../components/shared/InstallAppButton';

const ChatPage = ({ showToast }) => {
  const { token, user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNewRoomModal, setShowNewRoomModal] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [typingUsers, setTypingUsers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [unifiedOverview, setUnifiedOverview] = useState(null);
  const [featuredRooms, setFeaturedRooms] = useState([]);
  
  // New state for chat room controls
  const [showRoomControls, setShowRoomControls] = useState(false);
  const [databaseMode, setDatabaseMode] = useState('personal');
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [contentFilters, setContentFilters] = useState(['all']);
  
  // Topic rooms and profile
  const [topicRooms, setTopicRooms] = useState([]);
  const [showTopicBrowser, setShowTopicBrowser] = useState(false);
  const [viewingProfile, setViewingProfile] = useState(null);
  const [newRoomTopic, setNewRoomTopic] = useState('');
  const [newRoomCategoryId, setNewRoomCategoryId] = useState('');
  const [roomSearchResults, setRoomSearchResults] = useState([]);
  
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  
  // Fetch categories for room controls
  const fetchCategories = useCallback(() => {
    if (!token) return;
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/categories`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function() {
      if (x.status === 200) {
        try { setCategories(JSON.parse(x.responseText) || []); } catch (e) {}
      }
    };
    x.send();
  }, [token]);
  
  // Fetch search results for map display — responds to all filter controls
  const fetchRoomResults = useCallback(() => {
    if (!token || selectedCategories.length === 0) {
      setRoomSearchResults([]);
      return;
    }
    const dbParam = databaseMode === 'worldwide' ? '&scope=worldwide' : '';
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/ultimate-search?category_ids=${selectedCategories.join(',')}&limit=50${dbParam}`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function() {
      if (x.status === 200) {
        try {
          const data = JSON.parse(x.responseText);
          let results = data.results || [];
          // Apply content filters client-side
          if (!contentFilters.includes('all') && contentFilters.length > 0) {
            results = results.filter(r => contentFilters.includes(r.article_type));
          }
          setRoomSearchResults(results);
        } catch (e) {}
      }
    };
    x.send();
  }, [token, selectedCategories, databaseMode, contentFilters]);
  
  useEffect(() => {
    if (showRoomControls && categories.length === 0) {
      fetchCategories();
    }
  }, [showRoomControls, fetchCategories, categories.length]);
  
  useEffect(() => {
    if (showRoomControls) {
      fetchRoomResults();
    }
  }, [showRoomControls, selectedCategories, databaseMode, contentFilters, fetchRoomResults]);

  // Fetch rooms - MUST BE DEFINED BEFORE joinTopicRoom uses it
  const fetchRooms = useCallback(() => {
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/chat/rooms`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function() {
      if (x.status === 200) {
        try { setRooms(JSON.parse(x.responseText).rooms || []); } catch (e) {}
      }
      setLoading(false);
    };
    x.onerror = function() { setLoading(false); };
    x.send();
  }, [token]);

  // Fetch topic-based rooms
  const fetchTopicRooms = useCallback(() => {
    if (!token) return;
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/chat/rooms/topic`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function() {
      if (x.status === 200) {
        try { setTopicRooms(JSON.parse(x.responseText).rooms || []); } catch (e) {}
      }
    };
    x.send();
  }, [token]);

  // Fetch user profile
  const fetchUserProfile = useCallback((userId) => {
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/chat/user-profile/${userId}`, true);
    x.onload = function() {
      if (x.status === 200) {
        try { setViewingProfile(JSON.parse(x.responseText)); } catch (e) {}
      }
    };
    x.send();
  }, []);

  // Join a topic room
  const joinTopicRoom = useCallback((roomId) => {
    if (!token) return;
    const x = new XMLHttpRequest();
    x.open('POST', `${API}/chat/rooms/${roomId}/join`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function() {
      if (x.status === 200) {
        fetchRooms();
        setActiveRoom(roomId);
        showToast && showToast('Joined topic room!', 'success');
      }
    };
    x.send();
  }, [token, fetchRooms, showToast]);

  useEffect(() => {
    if (showTopicBrowser) fetchTopicRooms();
  }, [showTopicBrowser, fetchTopicRooms]);


  // Fetch unified chat overview (groups + DMs)
  const fetchUnifiedOverview = useCallback(() => {
    if (!token) return;
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/unified-chat/overview`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function() {
      if (x.status === 200) {
        try { setUnifiedOverview(JSON.parse(x.responseText)); } catch (e) {}
      }
    };
    x.send();
  }, [token]);

  const fetchMessages = useCallback((roomId) => {
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/chat/rooms/${roomId}/messages`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function() {
      if (x.status === 200) {
        try { setMessages(JSON.parse(x.responseText).messages || []); } catch (e) {}
      }
    };
    x.send();
  }, [token]);

  const fetchOnlineUsers = useCallback(() => {
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/chat/online`, true);
    x.onload = function() {
      if (x.status === 200) {
        try { setOnlineUsers(JSON.parse(x.responseText).online_users || []); } catch (e) {}
      }
    };
    x.send();
  }, []);

  const connectWebSocket = useCallback((roomId) => {
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://');
    wsRef.current = new WebSocket(`${wsUrl}/chat/ws/${roomId}?token=${token}`);

    wsRef.current.onopen = () => {
      console.log('Chat WebSocket connected');
    };

    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'message') {
        setMessages(prev => [...prev, {
          id: data.id,
          user_id: data.user_id,
          username: data.username,
          content: data.content,
          message_type: data.message_type,
          timestamp: data.timestamp,
          is_own: data.user_id === user?.id
        }]);
      } else if (data.type === 'typing') {
        if (data.is_typing) {
          setTypingUsers(prev => [...new Set([...prev, data.username])]);
        } else {
          setTypingUsers(prev => prev.filter(u => u !== data.username));
        }
      } else if (data.type === 'user_joined') {
        showToast(`${data.username} joined the chat`, 'info');
      } else if (data.type === 'user_left') {
        showToast(`${data.username} left the chat`, 'info');
      }
    };

    wsRef.current.onclose = () => {
      console.log('Chat WebSocket closed');
    };

    wsRef.current.onerror = (error) => {
      console.error('Chat WebSocket error:', error);
    };
  }, [token, user?.id, showToast]);

  useEffect(() => {
    const loadInitial = async () => {
      await Promise.all([fetchRooms(), fetchOnlineUsers(), fetchUnifiedOverview()]);
    };
    loadInitial();
    const interval = setInterval(fetchOnlineUsers, 30000);
    const overviewInterval = setInterval(fetchUnifiedOverview, 60000);
    return () => {
      clearInterval(interval);
      clearInterval(overviewInterval);
    };
  }, [fetchRooms, fetchOnlineUsers, fetchUnifiedOverview]);

  useEffect(() => {
    if (activeRoom) {
      const loadRoom = async () => {
        await fetchMessages(activeRoom.id);
        connectWebSocket(activeRoom.id);
      };
      loadRoom();
    }
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [activeRoom, fetchMessages, connectWebSocket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = () => {
    if (!newMessage.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'message',
      content: newMessage,
      message_type: 'text'
    }));

    setNewMessage('');
  };

  const handleTyping = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    wsRef.current.send(JSON.stringify({
      type: 'typing',
      is_typing: true
    }));

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'typing',
          is_typing: false
        }));
      }
    }, 2000);
  };

  const createRoom = () => {
    if (!newRoomName.trim()) {
      showToast('Please enter a room name', 'error');
      return;
    }
    const roomType = newRoomTopic ? 'topic' : 'group';
    const x = new XMLHttpRequest();
    x.open('POST', `${API}/chat/rooms`, true);
    x.setRequestHeader('Content-Type', 'application/json');
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function() {
      if (x.status >= 200 && x.status < 300) {
        try {
          const data = JSON.parse(x.responseText);
          showToast('Room created!', 'success');
          setShowNewRoomModal(false);
          setNewRoomName('');
          setNewRoomTopic('');
          setNewRoomCategoryId('');
          fetchRooms();
          setActiveRoom({ id: data.id, name: data.name });
        } catch (e) {}
      } else {
        showToast('Failed to create room', 'error');
      }
    };
    x.onerror = function() { showToast('Failed to create room', 'error'); };
    x.send(JSON.stringify({
      name: newRoomName,
      room_type: roomType,
      member_ids: [],
      topic: newRoomTopic || null,
      category_id: newRoomCategoryId || null
    }));
  };

  // Fetch featured rooms
  useEffect(() => {
    if (!token) return;
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/chat/featured-rooms`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = () => {
      if (x.status === 200) {
        try { setFeaturedRooms(JSON.parse(x.responseText).rooms || []); } catch {}
      }
    };
    x.send();
  }, [token]);

  const setAsFeatured = (roomId) => {
    if (!user?.is_admin) return;
    const currentIds = featuredRooms.map(r => r.id);
    if (currentIds.includes(roomId)) {
      // Remove
      const newIds = currentIds.filter(id => id !== roomId);
      updateFeaturedRooms(newIds);
    } else if (currentIds.length < 6) {
      // Add
      updateFeaturedRooms([...currentIds, roomId]);
    } else {
      showToast('Maximum 6 featured rooms', 'warning');
    }
  };

  const updateFeaturedRooms = (roomIds) => {
    const x = new XMLHttpRequest();
    x.open('PUT', `${API}/chat/featured-rooms`, true);
    x.setRequestHeader('Content-Type', 'application/json');
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = () => {
      if (x.status === 200) {
        showToast('Featured rooms updated!', 'success');
        // Refresh featured
        const x2 = new XMLHttpRequest();
        x2.open('GET', `${API}/chat/featured-rooms`, true);
        x2.setRequestHeader('Authorization', `Bearer ${token}`);
        x2.onload = () => { try { setFeaturedRooms(JSON.parse(x2.responseText).rooms || []); } catch {} };
        x2.send();
      }
    };
    x.send(JSON.stringify({ room_ids: roomIds }));
  };

  return (
    <div data-testid="chat-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Install App Banner */}
      <InstallAppButton variant="banner" />

      {/* Main Chat Card */}
      <div className="card" style={{ minHeight: 400, display: 'flex', flexDirection: 'column' }}>
      <div className="card-header">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className="fas fa-comments" style={{ color: '#047857' }} /> Chat
          <span style={{ fontSize: '0.8rem', color: '#10b981', fontWeight: 400 }}>
            {onlineUsers.length} pilot{onlineUsers.length !== 1 ? 's' : ''} online
          </span>
        </h2>
      </div>

      <div style={{ display: 'flex', flex: 1, gap: 20, overflow: 'hidden' }}>
        {/* Rooms Sidebar */}
        <div style={{
          width: 280,
          background: '#475569',
          borderRadius: 12,
          padding: 15,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
            <h3 style={{ color: '#047857', margin: 0 }}>Chat Rooms</h3>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn" data-testid="browse-topic-rooms-btn"
                style={{ padding: '6px 10px', fontSize: '0.75rem', background: showTopicBrowser ? 'rgba(252, 211, 77,0.3)' : 'rgba(252, 211, 77,0.15)', color: '#c4b5fd', border: '1px solid rgba(252, 211, 77,0.3)', borderRadius: 6 }}
                onClick={() => setShowTopicBrowser(!showTopicBrowser)}>
                Topics
              </button>
              <button className="btn btn-primary"
                style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                onClick={() => setShowNewRoomModal(true)}>
                + New
              </button>
            </div>
          </div>

          {/* Topic Rooms Browser */}
          {showTopicBrowser && (
            <div style={{ marginBottom: 12, padding: 10, background: 'rgba(252, 211, 77,0.08)', borderRadius: 10, border: '1px solid rgba(252, 211, 77,0.2)' }}>
              <h4 style={{ color: '#c4b5fd', fontSize: '0.82rem', margin: '0 0 8px 0' }}>
                <i className="fas fa-hashtag" style={{ marginRight: 5 }} /> Topic Rooms
              </h4>
              {topicRooms.length === 0 ? (
                <p style={{ color: '#71717a', fontSize: '0.75rem' }}>No topic rooms yet. Create one with the + New button!</p>
              ) : (
                <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                  {topicRooms.map(room => (
                    <div key={room.id} onClick={() => joinTopicRoom(room.id)}
                      style={{ padding: '8px 10px', background: 'rgba(0,0,0,0.2)', borderRadius: 8, marginBottom: 6, cursor: 'pointer', border: '1px solid rgba(252, 211, 77,0.15)' }}
                      data-testid={`topic-room-${room.id}`}>
                      <div style={{ color: '#334155', fontSize: '0.82rem', fontWeight: 600 }}>{room.name}</div>
                      <div style={{ display: 'flex', gap: 10, marginTop: 3 }}>
                        {room.topic && <span style={{ color: '#047857', fontSize: '0.68rem' }}>#{room.topic}</span>}
                        <span style={{ color: '#71717a', fontSize: '0.68rem' }}>{room.member_count} members</span>
                        <span style={{ color: '#10b981', fontSize: '0.68rem' }}>{room.online_count} online</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <p style={{ color: '#a1a1aa', textAlign: 'center' }}>Loading...</p>
            ) : rooms.length === 0 ? (
              <p style={{ color: '#a1a1aa', textAlign: 'center', fontSize: '0.9rem' }}>
                No chat rooms yet. Create one!
              </p>
            ) : (
              rooms.map(room => (
                <div
                  key={room.id}
                  onClick={() => setActiveRoom(room)}
                  style={{
                    background: activeRoom?.id === room.id 
                      ? 'linear-gradient(135deg, rgba(252, 211, 77, 0.3), rgba(244, 114, 182, 0.2))'
                      : 'rgba(0,0,0,0.2)',
                    padding: 12,
                    borderRadius: 10,
                    marginBottom: 10,
                    cursor: 'pointer',
                    border: activeRoom?.id === room.id ? '1px solid #FCD34D' : '1px solid transparent',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#0F172A', fontWeight: 600 }}>
                      {featuredRooms.some(f => f.id === room.id) && (
                        <i className="fas fa-star" style={{ color: '#f59e0b', marginRight: 5, fontSize: '0.7rem' }} />
                      )}
                      {room.name}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {user?.is_admin && (
                        <button onClick={(e) => { e.stopPropagation(); setAsFeatured(room.id); }}
                          data-testid={`feature-room-${room.id}`}
                          title={featuredRooms.some(f => f.id === room.id) ? 'Remove from featured' : 'Set as featured (admin)'}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: featuredRooms.some(f => f.id === room.id) ? '#f59e0b' : '#555', fontSize: '0.75rem' }}>
                          <i className={featuredRooms.some(f => f.id === room.id) ? 'fas fa-star' : 'far fa-star'} />
                        </button>
                      )}
                      {room.unread_count > 0 && (
                        <span style={{ background: '#FEF08A', color: '#0F172A', padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 700 }}>
                          {room.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ color: '#a1a1aa', fontSize: '0.75rem', marginTop: 4 }}>
                    {room.member_count} members • {room.online_count || 0} online
                  </div>
                  {room.last_message && (
                    <div style={{ color: '#71717a', fontSize: '0.75rem', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>{room.last_message.username}:</strong> {room.last_message.content}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Online Users */}
          <div style={{ marginTop: 15, paddingTop: 15, borderTop: '1px solid rgba(252, 211, 77, 0.3)' }}>
            <h4 style={{ color: '#10b981', fontSize: '0.85rem', marginBottom: 10 }}>🟢 Online Now</h4>
            <div style={{ maxHeight: 100, overflowY: 'auto' }}>
              {onlineUsers.slice(0, 5).map(u => (
                <div key={u.id} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 8, 
                  marginBottom: 6,
                  fontSize: '0.8rem',
                  color: '#a1a1aa'
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
                  {u.callsign || u.username}
                </div>
              ))}
              {onlineUsers.length > 5 && (
                <div style={{ color: '#71717a', fontSize: '0.75rem' }}>
                  +{onlineUsers.length - 5} more
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: 12,
          overflow: 'hidden'
        }}>
          {activeRoom ? (
            <>
              {/* Chat Header with Controls Toggle */}
              <div style={{
                padding: 15,
                background: 'rgba(252, 211, 77, 0.2)',
                borderBottom: '1px solid rgba(252, 211, 77, 0.3)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h3 style={{ color: '#0F172A', margin: 0 }}>{activeRoom.name}</h3>
                  <span style={{ color: '#a1a1aa', fontSize: '0.8rem' }}>
                    {activeRoom.member_count} members
                  </span>
                </div>
                <button
                  onClick={() => setShowRoomControls(!showRoomControls)}
                  data-testid="toggle-room-controls"
                  style={{
                    background: showRoomControls 
                      ? 'linear-gradient(135deg, #FCD34D, #047857)' 
                      : 'rgba(252, 211, 77, 0.3)',
                    border: '1px solid rgba(252, 211, 77, 0.5)',
                    borderRadius: 8,
                    padding: '8px 14px',
                    color: '#0F172A',
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  🗺️ Map & Controls
                </button>
              </div>
              
              {/* Room Controls Panel (Map, Categories, Database Toggle) */}
              {showRoomControls && (
                <div style={{
                  padding: 15,
                  background: 'rgba(253, 251, 247, 0.8)',
                  borderBottom: '1px solid rgba(252, 211, 77, 0.2)',
                  maxHeight: 500,
                  overflowY: 'auto'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 15 }}>
                    {/* Database Toggle */}
                    <DatabaseToggle
                      value={databaseMode}
                      onChange={setDatabaseMode}
                      userList={[]}
                    />
                    
                    {/* Category Checkboxes */}
                    <CategoryCheckboxes
                      categories={categories}
                      selectedIds={selectedCategories}
                      onChange={setSelectedCategories}
                    />
                    
                    {/* URL Preference Filters */}
                    <URLPreferenceFilters
                      filters={contentFilters}
                      onChange={setContentFilters}
                    />
                  </div>
                  
                  {/* Interactive Map */}
                  <InteractiveMapPanel
                    results={roomSearchResults}
                    onMarkerClick={(result) => {
                      window.open(result.url, '_blank');
                    }}
                    height={200}
                  />
                </div>
              )}

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 15 }}>
                {messages.map((msg, i) => (
                  <div
                    key={msg.id || i}
                    style={{
                      display: 'flex',
                      flexDirection: msg.is_own ? 'row-reverse' : 'row',
                      marginBottom: 15
                    }}
                  >
                    <div style={{
                      maxWidth: '70%',
                      background: msg.is_own 
                        ? 'linear-gradient(135deg, #FCD34D, #047857)'
                        : 'rgba(0,0,0,0.3)',
                      padding: '10px 15px',
                      borderRadius: msg.is_own ? '15px 15px 0 15px' : '15px 15px 15px 0'
                    }}>
                      {!msg.is_own && (
                        <div style={{ color: '#047857', fontSize: '0.75rem', fontWeight: 600, marginBottom: 4, cursor: 'pointer', textDecoration: 'underline dotted' }}
                          onClick={() => msg.user_id && fetchUserProfile(msg.user_id)}
                          data-testid={`msg-username-${msg.id}`}
                          title="Click to view profile">
                          {msg.username}
                        </div>
                      )}
                      <MessageContent text={msg.content} style={{ color: '#0F172A' }} />
                      <div style={{ color: '#475569', fontSize: '0.65rem', marginTop: 4, textAlign: msg.is_own ? 'right' : 'left' }}>
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Typing Indicator */}
              {typingUsers.length > 0 && (
                <div style={{ padding: '5px 15px', color: '#a1a1aa', fontSize: '0.8rem', fontStyle: 'italic' }}>
                  {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                </div>
              )}

              {/* Message Input */}
              <div style={{ padding: 15, background: 'rgba(14, 165, 233, 0.08)', display: 'flex', gap: 10, borderTop: '1px solid rgba(16, 185, 129, 0.18)' }}>
                <textarea
                  className="input-field"
                  placeholder="Type a message... (Enter to send, Shift+Enter for new line)"
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    handleTyping();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  rows={3}
                  style={{ flex: 1, resize: 'none', fontFamily: 'inherit', minHeight: 60 }}
                  data-testid="chat-input"
                />
                <button
                  className="btn btn-primary"
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  data-testid="send-message-btn"
                >
                  Send
                </button>
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', padding: 30 }}>
              <i className="fas fa-plane" style={{ fontSize: '3.5rem', color: '#047857', marginBottom: 20, opacity: 0.6 }} />
              <h3 style={{ color: '#c4b5fd', marginBottom: 8 }}>Welcome to Chat</h3>
              <p style={{ color: '#71717a', fontSize: '0.92rem', textAlign: 'center', maxWidth: 400, lineHeight: 1.6 }}>
                Select a room to start chatting, or create a new one. My father said the trick to a good conversation is staying calm under pressure. I use the same technique when the WiFi goes out.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* New Room Modal */}
      {showNewRoomModal && (
        <div className="modal-overlay" onClick={() => setShowNewRoomModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h2>Create Chat Room</h2>
              <button className="modal-close" onClick={() => setShowNewRoomModal(false)}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              <input
                className="input-field"
                placeholder="Room Name"
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
                style={{ marginBottom: 10 }}
                data-testid="new-room-name-input"
              />
              <input className="input-field"
                placeholder="Topic (optional, e.g. 'Welding Techniques')"
                value={newRoomTopic}
                onChange={(e) => setNewRoomTopic(e.target.value)}
                style={{ marginBottom: 10 }}
                data-testid="new-room-topic-input"
              />
              <select value={newRoomCategoryId}
                onChange={(e) => setNewRoomCategoryId(e.target.value)}
                style={{ width: '100%', marginBottom: 15, padding: '10px 14px', background: 'rgba(255, 255, 255,0.8)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: '#334155', fontSize: '0.9rem' }}
                data-testid="new-room-category-select">
                <option value="">No category (general)</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <button
                className="btn btn-primary"
                onClick={createRoom}
                style={{ width: '100%' }}
                data-testid="create-room-btn"
              >
                Create Room
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Book Advertisement */}
      <SmallBookAd variant={2} />

      {/* User Profile Modal */}
      {viewingProfile && (
        <div className="modal-overlay" onClick={() => setViewingProfile(null)} style={{ zIndex: 1000 }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, padding: 0 }} data-testid="profile-modal">
            <div style={{ background: 'linear-gradient(135deg, rgba(253, 224, 71,0.3), rgba(245, 158, 11,0.2))', padding: 20, borderRadius: '12px 12px 0 0', position: 'relative' }}>
              <button onClick={() => setViewingProfile(null)} style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', color: '#a1a1aa', fontSize: '1.2rem', cursor: 'pointer' }} data-testid="close-profile-modal">&times;</button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'linear-gradient(135deg, #FDE047, #FDE68A)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: '#0F172A', fontWeight: 700, border: '2px solid rgba(253, 224, 71,0.5)', overflow: 'hidden' }}>
                  {viewingProfile.avatar_url
                    ? <img src={viewingProfile.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : (viewingProfile.username || '?')[0].toUpperCase()}
                </div>
                <div>
                  <h3 style={{ color: '#0F172A', margin: 0, fontSize: '1.1rem' }}>{viewingProfile.username}</h3>
                  {viewingProfile.callsign && <p style={{ color: '#047857', fontSize: '0.75rem', margin: '2px 0' }}>"{viewingProfile.callsign}"</p>}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: viewingProfile.is_online ? '#10b981' : '#71717a' }} />
                    <span style={{ color: viewingProfile.is_online ? '#10b981' : '#71717a', fontSize: '0.7rem' }}>{viewingProfile.is_online ? 'Online' : 'Offline'}</span>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ padding: 20 }}>
              {viewingProfile.bio && <p style={{ color: '#d1d5db', fontSize: '0.85rem', lineHeight: 1.5, marginBottom: 12 }}>{viewingProfile.bio}</p>}
              {(viewingProfile.interests || []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 12 }}>
                  {viewingProfile.interests.map((i, idx) => (
                    <span key={idx} style={{ background: 'rgba(253, 224, 71,0.2)', border: '1px solid rgba(253, 224, 71,0.3)', color: '#c4b5fd', padding: '3px 8px', borderRadius: 10, fontSize: '0.7rem' }}>{i}</span>
                  ))}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ background: 'rgba(253, 224, 71,0.1)', padding: 10, borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ color: '#FDE047', fontSize: '1.1rem', fontWeight: 700 }}>{viewingProfile.categories_count || 0}</div>
                  <div style={{ color: '#71717a', fontSize: '0.65rem' }}>Categories</div>
                </div>
                <div style={{ background: 'rgba(16,185,129,0.1)', padding: 10, borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ color: '#10b981', fontSize: '1.1rem', fontWeight: 700 }}>{viewingProfile.protocols_count || 0}</div>
                  <div style={{ color: '#71717a', fontSize: '0.65rem' }}>Protocols</div>
                </div>
              </div>
              {viewingProfile.joined && (
                <p style={{ color: '#71717a', fontSize: '0.7rem', textAlign: 'center', marginTop: 10 }}>Joined {new Date(viewingProfile.joined).toLocaleDateString()}</p>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
      
      {/* Cockpit Dashboard — Search, Map, Statistics, Analytics, Duplicates */}
      <CockpitDashboard showToast={showToast} />
    </div>
  );
};

export default ChatPage;
